import { useState } from "react";

import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";
import NewPasswordForm from "@/components/auth/NewPasswordForm";
import VerifyOtpForm from "@/components/auth/VerifyOtpForm";
import { authService } from "@/services/authService";
import {
  ForgotPasswordData,
  ResetPasswordData,
  VerifyOtpData,
} from "@/types/auth";

type Step = "email" | "otp" | "password";

export default function ForgotPasswordPage() {
  const [currentStep, setCurrentStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [verifiedCode, setVerifiedCode] = useState(""); // Store the verified OTP code
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Step 1: Send OTP to email
  const handleSendOtp = async (data: ForgotPasswordData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use real API to send reset code
      const response = await authService.sendResetCode(data.email);

      if (response.success) {
        console.log("Reset code sent to:", data.email);
        setEmail(data.email);
        setCurrentStep("otp");
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to send reset code",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (data: VerifyOtpData) => {
    console.log("handleVerifyOtp called with data:", data);
    try {
      setIsLoading(true);
      setError(null);

      // Use real API to verify reset code
      const response = await authService.verifyOTP(data.email, data.otp);
      console.log("API response received:", response);

      if (response.success && response.token) {
        console.log(
          "OTP verified successfully, token received:",
          response.token,
        );
        setVerifiedCode(data.otp); // Store the verified code instead of token
        console.log("About to change step to password");
        setCurrentStep("password");
        console.log(
          "Step changed to password, current step should now be 'password'",
        );
      } else if (response.success && !response.token) {
        console.log(
          "API verification successful but no token received. Response:",
          response,
        );
        // Store the verified code since verification was successful
        setVerifiedCode(data.otp);
        setCurrentStep("password");
        console.log("Proceeding to password step with verified code");
      } else {
        console.log("API response was not successful:", response);
        throw new Error(response.message || "Verification failed");
      }
    } catch (error) {
      console.error("Error in handleVerifyOtp:", error);
      setError(
        error instanceof Error ? error.message : "Failed to verify code",
      );
    } finally {
      setIsLoading(false);
      console.log("handleVerifyOtp completed, isLoading set to false");
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (data: ResetPasswordData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use the verified code with email and password for Laravel API
      const response = await authService.resetPasswordWithCode(
        email,
        verifiedCode,
        data.password,
        data.confirmPassword,
      );

      if (response.success) {
        console.log("Password reset successful for:", email);
        setIsSuccess(true);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to reset password",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    try {
      setIsResending(true);
      setError(null);

      // Use real API to resend reset code
      const response = await authService.sendResetCode(email);

      if (response.success) {
        console.log("Reset code resent to:", email);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to resend reset code",
      );
    } finally {
      setIsResending(false);
    }
  };

  // Navigation handlers
  const handleBackToEmail = () => {
    setCurrentStep("email");
    setError(null);
  };

  const handleBackToOtp = () => {
    setCurrentStep("otp");
    setError(null);
  };

  const renderCurrentStep = () => {
    console.log(
      "renderCurrentStep called - Current step:",
      currentStep,
      "Email:",
      email,
      "VerifiedCode:",
      verifiedCode,
    );

    switch (currentStep) {
      case "email":
        console.log("Rendering email step");
        return (
          <ForgotPasswordForm
            error={error || undefined}
            isLoading={isLoading}
            onSubmit={handleSendOtp}
          />
        );

      case "otp":
        console.log("Rendering otp step");
        return (
          <VerifyOtpForm
            email={email}
            error={error || undefined}
            isLoading={isLoading}
            isResending={isResending}
            onBack={handleBackToEmail}
            onResendOtp={handleResendOtp}
            onSubmit={handleVerifyOtp}
          />
        );

      case "password":
        console.log("Rendering password step");
        return (
          <NewPasswordForm
            email={email}
            error={error || undefined}
            isLoading={isLoading}
            isSuccess={isSuccess}
            onBack={handleBackToOtp}
            onSubmit={handleResetPassword}
            otp=""
          />
        );

      default:
        console.log("Rendering default (null)");
        return null;
    }
  };

  return renderCurrentStep();
}
